import { useEffect, useId, useRef, useState } from 'react'
import { Badge, Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger } from '@repo/ui'
import { Plus, Tag, X } from 'lucide-react'
import { createTag, fetchTags } from '@/api/tags'
import type { OrgTag } from '@/api/tags'

export type ContentTag = Pick<OrgTag, 'tagId' | 'name' | 'colour'>

export function ContentTagPicker({
  value,
  onChange,
  disabled,
  creating,
  onCreatingChange
}: {
  value: Array<ContentTag>
  onChange: (tags: Array<ContentTag>) => void
  disabled: boolean
  creating: boolean
  onCreatingChange: (creating: boolean) => void
}) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [tags, setTags] = useState<Array<OrgTag>>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string>()
  const [createError, setCreateError] = useState<string>()
  const [request, setRequest] = useState(0)
  const mounted = useRef(false)
  const createPending = useRef(false)
  const searchInput = useRef<HTMLInputElement>(null)
  const query = search.trim().toLowerCase()
  const locked = disabled || creating
  const matching = tags.filter(
    (tag) => tag.name.toLowerCase().includes(query) || tag.description?.toLowerCase().includes(query)
  )
  const canCreate =
    query !== '' && !loading && !loadError && !tags.some((tag) => tag.name.trim().toLowerCase() === query)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!open) return
    let current = true
    void fetchTags()
      .then((result) => {
        if (current) setTags(result)
      })
      .catch((cause: unknown) => {
        if (current) setLoadError(cause instanceof Error ? cause.message : 'Could not load tags.')
      })
      .finally(() => {
        if (current) setLoading(false)
      })
    return () => {
      current = false
    }
  }, [open, request])

  async function handleCreate() {
    if (!canCreate || locked || createPending.current) return
    createPending.current = true
    onCreatingChange(true)
    setCreateError(undefined)
    try {
      const tag = await createTag(search)
      if (!mounted.current) return
      setTags((current) => [...current.filter((item) => item.tagId !== tag.tagId), tag])
      if (!value.some((item) => item.tagId === tag.tagId)) onChange([...value, tag])
      setSearch('')
    } catch (cause) {
      if (mounted.current)
        setCreateError(cause instanceof Error ? cause.message : 'Could not create this tag. Try again.')
    } finally {
      createPending.current = false
      if (mounted.current) onCreatingChange(false)
    }
  }

  useEffect(() => {
    if (open && !creating) searchInput.current?.focus()
  }, [open, creating])

  return (
    <fieldset className="min-w-0 space-y-2">
      <legend className="text-sm font-medium">Tags</legend>
      <p className="text-xs text-muted-foreground">Tags are shared across your organisation.</p>
      <div className="flex flex-wrap items-center gap-2">
        {value.map((tag) => (
          <Badge key={tag.tagId} variant="secondary" className="max-w-full gap-1 py-0.5 pr-1">
            <span className="min-w-0 break-all">{tag.name}</span>
            <button
              type="button"
              aria-label={`Remove tag ${tag.name}`}
              disabled={locked}
              onClick={() => onChange(value.filter((item) => item.tagId !== tag.tagId))}
              className="flex size-7 shrink-0 items-center justify-center rounded-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </Badge>
        ))}
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (creating) return
            setOpen(next)
            if (next) {
              setSearch('')
              setLoading(true)
              setLoadError(undefined)
              setCreateError(undefined)
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={locked}>
              <Tag className="size-4" aria-hidden="true" />
              Add tags
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 p-2"
          >
            <Input
              ref={searchInput}
              aria-label="Search tags"
              placeholder="Search or create a tag"
              value={search}
              disabled={locked}
              onChange={(event) => {
                setSearch(event.target.value)
                setCreateError(undefined)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault()
              }}
            />
            <div className="max-h-52 min-h-0 overflow-y-auto overscroll-contain">
              {loading ? (
                <p role="status" className="p-2 text-sm text-muted-foreground">
                  Loading tags...
                </p>
              ) : loadError ? (
                <div role="alert" className="space-y-2 p-2">
                  <p className="text-sm text-destructive">{loadError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLoading(true)
                      setLoadError(undefined)
                      setRequest((current) => current + 1)
                    }}
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                <>
                  {matching.length === 0 && (
                    <p className="p-2 text-sm text-muted-foreground">
                      {query ? 'No matching tags.' : 'No tags yet. Type a name to create one.'}
                    </p>
                  )}
                  {matching.map((tag) => (
                    <label
                      key={tag.tagId}
                      htmlFor={`${id}-${tag.tagId}`}
                      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-accent"
                    >
                      <Checkbox
                        id={`${id}-${tag.tagId}`}
                        aria-label={tag.name}
                        checked={value.some((item) => item.tagId === tag.tagId)}
                        disabled={locked}
                        onCheckedChange={(checked) =>
                          onChange(
                            checked === true ? [...value, tag] : value.filter((item) => item.tagId !== tag.tagId)
                          )
                        }
                      />
                      <span className="min-w-0 break-words">{tag.name}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
            {createError && (
              <p role="alert" className="px-2 text-sm text-destructive">
                {createError}
              </p>
            )}
            {canCreate && (
              <Button
                type="button"
                variant="ghost"
                className="h-auto min-h-11 justify-start text-left whitespace-normal"
                aria-label={`Create tag ${search.trim()}`}
                disabled={locked}
                onClick={() => {
                  void handleCreate()
                }}
              >
                <Plus className="size-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0 break-all">{creating ? 'Creating tag...' : `Create “${search.trim()}”`}</span>
              </Button>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </fieldset>
  )
}
